/**
 * ReportPanelResizeWrapper
 *
 * Wraps a single dashboard grid cell so an authorised user (Infralytiqs admin
 * or super-admin) can:
 *
 *   1. RESIZE the panel by click-and-hold on a bottom-right corner handle.
 *      Width snaps to the 12-column grid; height snaps to one panel-row unit
 *      (REPORT_PANEL_ROW_PX). On release the new `{ w, h }` is persisted via
 *      `onResizeEnd` (the parent issues the PATCH).
 *
 *   2. REORDER the panel by click-and-hold on a top-left drag handle. The
 *      drag uses HTML5 native DnD with the panel itself as the drag image.
 *      Drop targets show a vertical insertion line on the left/right edge,
 *      chosen by the pointer's X position vs. the target's midpoint. On
 *      drop, the parent re-numbers `layout.y` for the dashboard panels and
 *      PATCHes any whose y changed.
 *
 * Visual feedback:
 *   - Both handles are faint by default, fully opaque on hover / drag.
 *   - During RESIZE drag the cell shows a dashed outline and a "w × h" tooltip.
 *   - During REORDER drag the source panel fades; targets show an insertion bar.
 *   - Esc cancels an in-progress RESIZE (revert to saved size).
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Box, Tooltip } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { panelMinHeightPx, REPORT_PANEL_ROW_PX } from './chartTimeRange';
import type { ReportDefinition } from '../Settings/reportDsl';
import {
  LAYOUT_MAX_W, LAYOUT_MIN_H, LAYOUT_MAX_H, layoutMinWidthForReport,
} from '../Settings/reportDsl';

/** Custom MIME so we don't collide with browser/file drags. */
export const REPORT_PANEL_DND_MIME = 'application/x-il-report-panel';

/** 12-column grid → clamp width / height to the bounds defined in reportDsl. */
const MAX_W = LAYOUT_MAX_W;
const MIN_H = LAYOUT_MIN_H;
const MAX_H = LAYOUT_MAX_H;

const DEFAULT_LAYOUT = { x: 0, y: 0, w: 12, h: 4 } as const;

interface ReportPanelResizeWrapperProps {
  report: ReportDefinition;
  /** True when the current user may resize / persist the panel layout. */
  canResize: boolean;
  /** True when the current user may drag-reorder panels. Usually same as canResize. */
  canReorder?: boolean;
  /** Called on pointer-up with the new layout (only if w/h actually changed). */
  onResizeEnd: (
    report: ReportDefinition,
    layout: { x: number; y: number; w: number; h: number },
  ) => void;
  /** Called when this panel becomes the drag source. */
  onReorderDragStart?: (reportId: string) => void;
  /** Called when this panel's reorder drag ends (drop or cancel). */
  onReorderDragEnd?: () => void;
  /** Called when a panel is dropped onto this one. */
  onReorderDrop?: (sourceReportId: string, targetReportId: string, position: 'before' | 'after') => void;
  /** True while another panel is being dragged for reorder (so we can show drop affordances). */
  isReorderActive?: boolean;
  /** True when this panel is the one being dragged. */
  isReorderSource?: boolean;
  children: React.ReactNode;
}

interface DragStart {
  pointerX: number;
  pointerY: number;
  widthPx: number;
  heightPx: number;
  startW: number;
  startH: number;
}

const ResizeHandleIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path
      d="M11 1 L1 11 M11 5 L5 11 M11 9 L9 11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const ReportPanelResizeWrapper: React.FC<ReportPanelResizeWrapperProps> = ({
  report, canResize, canReorder = false,
  onResizeEnd, onReorderDragStart, onReorderDragEnd, onReorderDrop,
  isReorderActive = false, isReorderSource = false,
  children,
}) => {
  const MIN_W = layoutMinWidthForReport(report);
  const savedLayout = report.layout || DEFAULT_LAYOUT;
  const savedW = Math.min(Math.max(savedLayout.w ?? 12, MIN_W), MAX_W);
  const savedH = Math.max(savedLayout.h ?? 4, MIN_H);

  const [draftW, setDraftW] = useState<number | null>(null);
  const [draftH, setDraftH] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  /** Vertical insertion indicator side while another panel is dragged over us. */
  const [insertionSide, setInsertionSide] = useState<'before' | 'after' | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const startRef = useRef<DragStart | null>(null);

  const activeW = draftW ?? savedW;
  const activeH = draftH ?? savedH;

  const finishResize = useCallback((commit: boolean) => {
    if (!startRef.current) return;
    const finalW = draftW ?? savedW;
    const finalH = draftH ?? savedH;
    setIsDragging(false);
    startRef.current = null;
    setDraftW(null);
    setDraftH(null);
    if (commit && (finalW !== savedW || finalH !== savedH)) {
      onResizeEnd(report, {
        x: savedLayout.x ?? 0,
        y: savedLayout.y ?? 0,
        w: finalW,
        h: finalH,
      });
    }
  }, [draftW, draftH, savedW, savedH, onResizeEnd, report, savedLayout.x, savedLayout.y]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!canResize) return;
    if (!containerRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    startRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      widthPx: rect.width,
      heightPx: rect.height,
      startW: savedW,
      startH: savedH,
    };
    setIsDragging(true);
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* unsupported */ }
  }, [canResize, savedW, savedH]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = startRef.current;
    if (!start) return;

    const dx = e.clientX - start.pointerX;
    const dy = e.clientY - start.pointerY;

    /**
     * Approximate `pxPerCol` from the current panel rect: panel width covers
     * `startW` grid columns + (startW-1) gaps, so this is slightly larger than
     * the bare column width, but it's the right unit for snapping based on the
     * panel's own footprint — i.e. dragging by one panel-width adds one column.
     */
    const pxPerCol = start.widthPx / Math.max(start.startW, 1);
    const newWApprox = (start.widthPx + dx) / pxPerCol;
    const nextW = Math.min(Math.max(Math.round(newWApprox), MIN_W), MAX_W);

    const newHApprox = (start.heightPx + dy) / REPORT_PANEL_ROW_PX;
    const nextH = Math.min(Math.max(Math.round(newHApprox), MIN_H), MAX_H);

    setDraftW((prev) => (prev === nextW ? prev : nextW));
    setDraftH((prev) => (prev === nextH ? prev : nextH));
  }, [MIN_W]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
    finishResize(true);
  }, [finishResize]);

  const handlePointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
    finishResize(false);
  }, [finishResize]);

  // Escape cancels the in-progress drag (reverts to saved size).
  useEffect(() => {
    if (!isDragging) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') finishResize(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDragging, finishResize]);

  /* -----------------------------------------------------------------------
   *  REORDER (HTML5 native DnD)
   *
   *  Only the small DragIndicator handle is `draggable`. On dragstart we set
   *  the panel itself as the drag image so the user sees a ghost matching the
   *  whole tile, and we broadcast our report id to the parent so sibling
   *  panels can show drop affordances + skip drop on ourselves.
   * --------------------------------------------------------------------- */
  const handleHandleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!canReorder || !report._id) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(REPORT_PANEL_DND_MIME, report._id);
    // Some browsers require at least one text/plain entry for DnD to "stick"
    try { e.dataTransfer.setData('text/plain', report._id); } catch { /* noop */ }
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      try {
        e.dataTransfer.setDragImage(
          containerRef.current,
          e.clientX - rect.left,
          e.clientY - rect.top,
        );
      } catch { /* setDragImage unsupported in some envs */ }
    }
    onReorderDragStart?.(report._id);
  }, [canReorder, report._id, onReorderDragStart]);

  const handleHandleDragEnd = useCallback(() => {
    onReorderDragEnd?.();
    setInsertionSide(null);
  }, [onReorderDragEnd]);

  const handleContainerDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isReorderActive || isReorderSource || !canReorder) return;
    if (!containerRef.current) return;
    // Only accept our custom MIME — but Chromium hides dataTransfer.types from
    // dragover for security in some cases, so we also tolerate any drag while
    // a reorder is active (the source check above already gates this).
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = containerRef.current.getBoundingClientRect();
    const nextSide: 'before' | 'after' = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
    setInsertionSide((prev) => (prev === nextSide ? prev : nextSide));
  }, [isReorderActive, isReorderSource, canReorder]);

  const handleContainerDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Ignore leaves that go to descendants (HTML5 DnD fires dragleave on
    // every nested element traversal). Only clear when the pointer truly
    // exits the wrapper.
    if (!containerRef.current) return;
    const next = e.relatedTarget as Node | null;
    if (next && containerRef.current.contains(next)) return;
    setInsertionSide(null);
  }, []);

  const handleContainerDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isReorderActive || isReorderSource || !canReorder || !report._id) {
      setInsertionSide(null);
      return;
    }
    e.preventDefault();
    const sourceId = e.dataTransfer.getData(REPORT_PANEL_DND_MIME)
      || e.dataTransfer.getData('text/plain');
    const side = insertionSide ?? 'after';
    setInsertionSide(null);
    if (sourceId && sourceId !== report._id) {
      onReorderDrop?.(sourceId, report._id, side);
    }
  }, [isReorderActive, isReorderSource, canReorder, insertionSide, onReorderDrop, report._id]);

  return (
    <Box
      ref={containerRef}
      onDragOver={canReorder ? handleContainerDragOver : undefined}
      onDragLeave={canReorder ? handleContainerDragLeave : undefined}
      onDrop={canReorder ? handleContainerDrop : undefined}
      sx={{
        gridColumn: { md: `span ${activeW}` },
        minWidth: 0,
        width: '100%',
        minHeight: panelMinHeightPx(activeH),
        height: '100%',
        position: 'relative',
        transition: isDragging ? 'none' : 'min-height 0.18s ease, opacity 0.15s ease',
        opacity: isReorderSource ? 0.4 : 1,
        // Reveal the (overlapping) drag handle on hover so it doesn't clobber
        // the title bar at rest. Anywhere a reorder is in progress, reveal
        // handles on every panel so the user can see drop targets.
        '&:hover .il-report-panel-drag-handle, & .il-report-panel-drag-handle.is-active': {
          opacity: 0.6,
        },
        '& .il-report-panel-drag-handle:hover': {
          opacity: 1,
        },
        ...(isDragging && {
          outline: (t) => `2px dashed ${t.palette.secondary.main}`,
          outlineOffset: 2,
          userSelect: 'none',
          borderRadius: 1,
        }),
      }}
    >
      {children}

      {/* Insertion indicator while a sibling is being dragged over us. */}
      {insertionSide && isReorderActive && !isReorderSource && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 3,
            bgcolor: 'primary.main',
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 4,
            ...(insertionSide === 'before' ? { left: -4 } : { right: -4 }),
          }}
        />
      )}

      {/* Drag-to-reorder handle (top-left). Hidden at rest, fades in on
          panel hover or whenever ANY reorder drag is in progress. */}
      {canReorder && (
        <Tooltip title="Drag to reorder" placement="top-start">
          <Box
            role="button"
            aria-label="Reorder panel"
            draggable
            onDragStart={handleHandleDragStart}
            onDragEnd={handleHandleDragEnd}
            className={`il-report-panel-drag-handle${isReorderActive ? ' is-active' : ''}`}
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              borderRadius: 0.5,
              color: 'text.secondary',
              bgcolor: (t) => (isReorderActive ? t.palette.action.hover : 'transparent'),
              opacity: 0,
              transition: 'opacity 0.15s ease, color 0.15s ease, background-color 0.15s ease',
              zIndex: 3,
            }}
          >
            <DragIndicator sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}

      {/* Resize handle (bottom-right). */}
      {canResize && (
        <Tooltip
          title={isDragging ? `${activeW} cols × ${activeH} rows` : 'Drag to resize panel'}
          placement="top-end"
        >
          <Box
            role="button"
            aria-label="Resize panel"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            sx={{
              position: 'absolute',
              right: 4,
              bottom: 4,
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'nwse-resize',
              borderRadius: 0.5,
              color: 'text.disabled',
              opacity: isDragging ? 1 : 0.55,
              transition: 'opacity 0.15s ease, color 0.15s ease',
              touchAction: 'none',
              zIndex: 3,
              '&:hover': { opacity: 1, color: 'secondary.main' },
            }}
          >
            <ResizeHandleIcon />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
